import i18next, { type i18n as I18nInstanceType, InitOptions } from 'i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
// import HttpBackend from 'i18next-http-backend'; // HTTP経由でリソースをロードする場合に推奨
import { $appHook } from '../../common/$appHook'
import { jaTranslation } from './locales/ja'
import { enTranslation } from './locales/en' // AppHook をインポート
import { I18N_COMMON_KEYS } from './i18nCommonKeys' // ★ 共通キーをインポート

/**
 * LocalStorage に言語設定を保存する際のキー
 */
const LOCAL_STORAGE_KEY = '$core.i18n.locale'

/**
 * 言語設定の型定義
 */
export type LangConfig = {
  /** 内部的な言語コード (例: 'ja', 'en') */
  value: string
  /** UI表示用の言語名 (例: '日本語', 'English') */
  label: string
  /** ブラウザ言語コード (navigator.language) とのマッチング用キー (例: ['ja', 'ja-JP']) */
  langKeys: string[]
}

/**
 * i18next の初期化オプション (内部利用)
 * @internal
 */
interface I18nInitializeOptions {
  selectableLangs: LangConfig[]
  fallbackLng?: string
  debug?: boolean
  /** i18next.init に渡す追加オプション */
  i18nextOptions?: InitOptions
}

/**
 * # $core.$i18n
 *
 * 多言語化 および 表示テキストを調整する際に利用します。
 * i18next を利用しているため 詳細なコントロールは https://www.i18next.com/ を参照してください。
 *
 * ## 主な機能
 * - **言語切り替え:** `setLang(lang)` メソッドでアプリケーションの言語を切り替えます。
 * - **利用可能な言語リスト:** `selectableLangs` プロパティで UI 用の言語リストを取得できます。
 * - **i18next インスタンス:** `i18nInstance` プロパティで i18next のインスタンスに直接アクセスできます。
 *   - 翻訳取得 (`t` 関数) など、i18next の豊富な機能はこちらを介して利用します。
 * - **翻訳の動的追加/更新:** `addOrUpdateTranslations(lng, resources, ns?)` 翻訳を追加・変更できます。
 * - **共通キー:** `commonKeys` または短いエイリアス `c` でアクセス可能 (`$core.$i18n.c.OK`)
 *
 * ## 翻訳の利用例 (Javascript, or VueComponent内)
 *
 * ```typescript
 * // i18next インスタンスを直接利用
 * const greetingTranslatedTextString = $core.$i18n.i18nInstance.t('greeting');
 * const okText = $core.$i18n.i18nInstance.t($core.$i18n.c.OK, 'OK'); // 共通キー利用
 * ```
 *
 * ```vue
 * <!-- Vue Template 内 (i18next-vue 登録後) -->
 * <p>{{ $t(['SpecialAppName.greeting', 'greeting'], 'ようこそ！ \{\{username\}\} さん', { username: $core.$embAuth.user.first_name }) }}</p>
 * <button>{{ $t($core.$i18n.c.SAVE, '保存') }}</button>
 * ```
 *
 * @coreDocPath $core/40.customize/404.i18n
 */
export class I18nService {
  /** 選択可能な言語設定のリスト */
  public allowedLangs: LangConfig[] = []
  /** i18next のインスタンス (初期化後に利用可能) */
  public i18nInstance!: I18nInstanceType
  /** 初期化が完了したかどうか */
  private _isInitialized = false
  /** 初期化処理中の Promise */
  private initializePromise: Promise<void> | null = null

  /**
   * i18n で使用する共通キーの定義。
   * `$core.$i18n.commonKeys.OK` のようにアクセスします。
   * 短いエイリアスとして `$core.$i18n.c` も利用可能です。
   */
  public readonly commonKeys = I18N_COMMON_KEYS

  /**
   * 共通キーへの短いエイリアス。
   * `$core.$i18n.c.OK` のようにアクセスします。
   */
  get c() {
    return this.commonKeys
  }

  /**
   * i18n サービスが初期化済みかどうかを返します。
   */
  get isInitialized(): boolean {
    return this._isInitialized
  }

  /**
   * 翻訳関数 t へのショートカット (読み取り専用)。
   * 注意: this コンテキストが bind されていないため、直接コールバック等には渡せません。
   * `$core.$i18n.i18nInstance.t` を直接使うか、`useTranslation` フックの利用を推奨します。
   * @deprecated `$core.$i18n.i18nInstance.t` または `useTranslation` を使用してください。
   */
  get t() {
    // console.warn("Accessing $core.$i18n.t is deprecated. Use $core.$i18n.i18nInstance.t or useTranslation hook.");
    if (!this.isInitialized) {
      // 初期化前のフォールバック
      return (key: string | string[], options?: any): string =>
        options?.defaultValue ?? (Array.isArray(key) ? key[0] : key)
    }
    return this.i18nInstance.t
  }

  /**
   * i18next サービスを非同期で初期化します。
   * CORE Framework によって内部的に呼び出されます。
   * @param options 初期化オプション
   * @internal
   */
  async initialize(options: I18nInitializeOptions): Promise<void> {
    // 重複実行防止
    if (this.initializePromise) return this.initializePromise
    if (this._isInitialized) return

    console.log('[i18n] Initializing service...')
    this.initializePromise = this._performInitialization(options)
      .catch((error) => {
        console.error('[i18n] Initialization failed:', error)
        this.initializePromise = null // エラー時もクリア
        throw error // エラーを再スロー
      })
      .then(() => {
        this.initializePromise = null // 成功時もクリア
        console.log('[i18n] Initialization process completed.')
      })

    return this.initializePromise
  }

  /**
   * 実際の初期化処理を実行します。
   * @param options 初期化オプション
   * @internal
   */
  private async _performInitialization(options: I18nInitializeOptions): Promise<void> {
    this.allowedLangs = options.selectableLangs
    const fallbackLng = options.fallbackLng || 'ja'
    const supportedLngs = this.allowedLangs.map((lang) => lang.value)

    this.i18nInstance = i18next.createInstance()

    const initOptions: InitOptions = {
      debug: options.debug ?? false,
      fallbackLng: fallbackLng,
      supportedLngs: supportedLngs,
      ns: ['translation'],
      defaultNS: 'translation',
      interpolation: { escapeValue: false }, // false for Vue
      detection: {
        order: ['localStorage', 'navigator'],
        caches: ['localStorage'],
        lookupLocalStorage: LOCAL_STORAGE_KEY,
      },
      // resources: { // ★ ハードコードされたリソースは削除 (外部から渡すか、HttpBackend等でロード)
      // },
      // resources オプションでデフォルト翻訳を読み込む
      resources: {
        ja: {
          translation: jaTranslation,
        },
        en: {
          translation: enTranslation,
        },
      },
      ...options.i18nextOptions, // 外部からのオプションをマージ (resources も上書き可能)
    }

    await this.i18nInstance
      .use(LanguageDetector)
      // .use(HttpBackend) // 必要なら追加
      .init(initOptions)

    // 初期化完了フック呼び出し
    try {
      await $appHook.emit('i18n:initialized', { i18nInstance: this.i18nInstance })
    } catch (hookError) {
      console.error('[i18n] Error during i18n:initialized hook:', hookError)
    }

    this._isInitialized = true
    console.log(
      `[i18n] Service initialized successfully. Language: ${this.i18nInstance.language}`,
    )
  }

  /**
   * アプリケーションの言語設定を変更します。
   * 変更後、i18next の 'languageChanged' イベントが発火します。
   * @param lang 変更後の言語コード (例: 'en', 'ja')。`selectableLangs` に含まれる必要があります。
   */
  async setLang(lang: string): Promise<void> {
    if (!this.isInitialized) {
      console.warn('[i18n] Service is not initialized yet. Cannot change language.')
      return
    }
    const supportedLngs = this.allowedLangs.map((l) => l.value)
    if (!supportedLngs.includes(lang)) {
      console.warn(
        `[i18n] Attempted to set unsupported language: "${lang}". Supported: ${supportedLngs.join(', ')}`,
      )
      return
    }
    if (this.i18nInstance.language === lang) {
      console.debug(`[i18n] Language is already set to "${lang}". Skipping change.`)
      return
    }

    try {
      console.log(`[i18n] Changing language to "${lang}"...`)
      await this.i18nInstance.changeLanguage(lang)
      // languageChanged イベントは i18next 内部で処理される
    } catch (error) {
      console.error(`[i18n] Failed to change language to "${lang}":`, error)
    }
  }

  /**
   * UI (言語選択ドロップダウンなど) で使用するための、
   * 利用可能な言語設定のリストを取得します。
   */
  get selectableLangs(): LangConfig[] {
    return this.allowedLangs
  }

  /**
   * 指定された言語と名前空間に翻訳リソースを追加または上書きします。
   * アプリケーション実行中に動的に翻訳を変更する場合などに使用します。
   *
   * @param lng 対象の言語コード (例: 'en', 'ja')
   * @param resources 追加/上書きする翻訳リソースオブジェクト (例: { key1: 'value1', nested: { key2: 'value2' } })
   * @param namespace 対象の名前空間 (デフォルト: 'translation')
   * @returns 追加/上書きが成功した場合は true、サービスが初期化されていない場合は false
   *
   * @example
   * // 英語の 'greeting' を更新し、新しいキー 'newKey' を追加
   * const success = $core.$i18n.addOrUpdateTranslations('en', {
   *   greeting: 'Hello there!',
   *   newKey: 'This is a new translation'
   * });
   * if (success) {
   *   console.log('Translations updated for English.');
   * }
   */
  addOrUpdateTranslations(
    lng: string,
    resources: Record<string, any>,
    namespace = 'translation',
  ): boolean {
    if (!this.isInitialized) {
      console.warn(
        '[i18n] Service is not initialized yet. Cannot add/update translations.',
      )
      return false
    }
    try {
      // 第4引数 deep = true, 第5引数 overwrite = true
      this.i18nInstance.addResourceBundle(lng, namespace, resources, true, true)
      console.log(`[i18n] Translations added/updated for lng="${lng}", ns="${namespace}"`)
      // 必要に応じてリソース変更イベントを emit する
      // $appHook.emit('i18n:resourcesUpdated', { lng, namespace });
      return true
    } catch (error) {
      console.error(
        `[i18n] Failed to add/update translations for lng="${lng}", ns="${namespace}":`,
        error,
      )
      return false
    }
  }
}

// --- シングルトンインスタンスのエクスポート ---
export const $i18n = new I18nService()
